import { Events } from './events';

type TourVideoResolution = '720p' | '1080p' | '4k';
type TourVideoContainer = 'mp4' | 'webm';

type TourVideoExportOptions = {
    tourId: string;
    resolution?: TourVideoResolution;
    container?: TourVideoContainer;
    frameRate?: number;
    includeAnnotations?: boolean;
    filename?: string;
    autoPlay?: boolean;
};

type ReconstructionTimelineVideoExportOptions = {
    container?: TourVideoContainer;
    resolution?: TourVideoResolution;
    frameRate?: number;
    includeAnnotations?: boolean;
    filename?: string;
};

type RecordingPolicy = {
    requestedContainer: TourVideoContainer;
    selectedContainer: TourVideoContainer;
    mimeType: string;
    fallbackUsed: boolean;
};

type RecordingState = {
    status: 'idle' | 'armed' | 'recording' | 'finalizing';
    tourId: string | null;
    requestedContainer: TourVideoContainer | null;
    selectedContainer: TourVideoContainer | null;
    mimeType: string | null;
    resolution: TourVideoResolution | null;
    frameRate: number;
    includeAnnotations: boolean;
    fallbackUsed: boolean;
};

type PendingRecording = {
    options: Required<Pick<TourVideoExportOptions, 'tourId' | 'resolution' | 'container' | 'frameRate' | 'includeAnnotations' | 'autoPlay'>> & {
        filename?: string;
    };
    policy: RecordingPolicy;
};

type ActiveRecording = {
    tourId: string;
    recorder: MediaRecorder;
    stop: () => Promise<void>;
    cancel: () => Promise<void>;
    cleanup: () => void;
    policy: RecordingPolicy;
    options: PendingRecording['options'];
};

type ReconstructionTimelineRecordingState = {
    status: 'idle' | 'armed' | 'recording' | 'finalizing';
    timelineId: string | null;
    duration: number;
    requestedContainer: TourVideoContainer | null;
    selectedContainer: TourVideoContainer | null;
    mimeType: string | null;
    resolution: TourVideoResolution | null;
    frameRate: number;
    includeAnnotations: boolean;
    fallbackUsed: boolean;
};

type PendingReconstructionTimelineRecording = {
    options: Required<Pick<ReconstructionTimelineVideoExportOptions, 'resolution' | 'container' | 'frameRate' | 'includeAnnotations'>> & {
        filename?: string;
        timelineId: string;
        duration: number;
    };
    policy: RecordingPolicy;
};

type ActiveReconstructionTimelineRecording = {
    timelineId: string;
    recorder: MediaRecorder;
    stop: () => Promise<void>;
    cancel: () => Promise<void>;
    cleanup: () => void;
    policy: RecordingPolicy;
    options: PendingReconstructionTimelineRecording['options'];
};

type ReconstructionTimelineSnapshot = {
    id?: string;
    duration?: number;
};

type TourPlaybackSnapshot = {
    status?: 'stopped' | 'playing' | 'paused';
    tourId?: string | null;
};

const RESOLUTION_PRESETS: Record<TourVideoResolution, { width: number; height: number }> = {
    '720p': { width: 1280, height: 720 },
    '1080p': { width: 1920, height: 1080 },
    '4k': { width: 3840, height: 2160 }
};

const MP4_MIME_TYPES = [
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4;codecs=avc1.64001F',
    'video/mp4'
];

const WEBM_MIME_TYPES = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm'
];

const sanitizeFileSegment = (value: string) => {
    const cleaned = (value ?? '').trim().replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return cleaned.length > 0 ? cleaned : 'tour';
};

const formatTimestamp = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}-${hours}${minutes}${seconds}`;
};

const isElementRendered = (element: Element | null) => {
    if (!element || !element.isConnected || (element as HTMLElement).hidden) {
        return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') {
        return false;
    }

    const opacity = Number.parseFloat(style.opacity || '1');
    if (Number.isFinite(opacity) && opacity <= 0) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
};

const drawAnnotationOverlay = (
    context: CanvasRenderingContext2D,
    sourceCanvas: HTMLCanvasElement,
    outputSize: { width: number; height: number }
) => {
    const overlay = document.getElementById('annotation-labels-overlay') as HTMLElement | null;
    if (!overlay || !isElementRendered(overlay)) {
        return false;
    }

    const overlayRect = overlay.getBoundingClientRect();
    if (overlayRect.width <= 0 || overlayRect.height <= 0) {
        return false;
    }

    const scaleX = outputSize.width / sourceCanvas.clientWidth;
    const scaleY = outputSize.height / sourceCanvas.clientHeight;

    context.save();
    context.scale(scaleX, scaleY);

    const leaders = overlay.querySelectorAll<SVGLineElement>('line.annotation-label-leader');
    leaders.forEach((line) => {
        const x1 = Number.parseFloat(line.getAttribute('x1') ?? '0');
        const y1 = Number.parseFloat(line.getAttribute('y1') ?? '0');
        const x2 = Number.parseFloat(line.getAttribute('x2') ?? '0');
        const y2 = Number.parseFloat(line.getAttribute('y2') ?? '0');
        const stroke = line.getAttribute('stroke') ?? 'rgba(255,255,255,0.75)';
        const strokeWidth = Number.parseFloat(line.getAttribute('stroke-width') ?? '1.5');

        context.strokeStyle = stroke;
        context.lineWidth = Number.isFinite(strokeWidth) ? strokeWidth : 1.5;
        context.beginPath();
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.stroke();
    });

    const labels = overlay.querySelectorAll<HTMLElement>('.annotation-label');
    labels.forEach((label) => {
        const style = window.getComputedStyle(label);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
            return;
        }

        const rect = label.getBoundingClientRect();
        const localX = rect.left - overlayRect.left;
        const localY = rect.top - overlayRect.top;

        context.fillStyle = style.backgroundColor || 'rgba(20,22,27,0.82)';
        context.strokeStyle = style.borderColor || 'rgba(255,255,255,0.24)';
        context.lineWidth = Number.parseFloat(style.borderWidth || '1') || 1;
        context.fillRect(localX, localY, rect.width, rect.height);
        context.strokeRect(localX, localY, rect.width, rect.height);

        const textElement = label.querySelector<HTMLElement>('.annotation-label-text');
        const badgeElement = label.querySelector<HTMLElement>('.annotation-label-exhibit');
        const labelText = textElement?.textContent?.trim() || '';
        const badgeText = badgeElement?.textContent?.trim() || '';

        context.fillStyle = style.color || '#ffffff';
        context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        context.textBaseline = 'middle';

        const paddingX = 8;
        const textY = localY + rect.height * 0.5;
        context.fillText(labelText, localX + paddingX, textY);

        if (badgeText.length > 0) {
            const labelWidth = context.measureText(labelText).width;
            const badgeX = localX + paddingX + labelWidth + 10;
            const badgeStyle = badgeElement ? window.getComputedStyle(badgeElement) : null;
            const badgeHeight = Math.max(16, rect.height - 8);
            const badgeWidth = Math.max(22, context.measureText(badgeText).width + 10);
            const badgeY = localY + (rect.height - badgeHeight) * 0.5;

            context.fillStyle = badgeStyle?.backgroundColor || 'rgba(255,197,58,0.24)';
            context.fillRect(badgeX, badgeY, badgeWidth, badgeHeight);

            context.fillStyle = badgeStyle?.color || style.color || '#ffffff';
            context.fillText(badgeText, badgeX + 5, textY);
        }
    });

    context.restore();
    return true;
};

const createSupportSnapshot = () => {
    const hasMediaRecorder = typeof MediaRecorder !== 'undefined';
    const hasMimeProbe = hasMediaRecorder && typeof MediaRecorder.isTypeSupported === 'function';
    const probe = (mimeType: string) => {
        if (!hasMimeProbe) {
            return false;
        }
        try {
            return MediaRecorder.isTypeSupported(mimeType);
        } catch {
            return false;
        }
    };

    const supportedMp4 = MP4_MIME_TYPES.filter(probe);
    const supportedWebm = WEBM_MIME_TYPES.filter(probe);

    return {
        available: hasMediaRecorder,
        hasMimeProbe,
        containers: {
            mp4: supportedMp4.length > 0,
            webm: supportedWebm.length > 0
        },
        mimeTypes: {
            mp4: supportedMp4,
            webm: supportedWebm
        }
    };
};

const buildRecordingPolicy = (requestedContainer: TourVideoContainer): RecordingPolicy | null => {
    const support = createSupportSnapshot();
    if (!support.available) {
        return null;
    }

    const requestedMimeTypes = requestedContainer === 'mp4' ? MP4_MIME_TYPES : WEBM_MIME_TYPES;
    const fallbackMimeTypes = requestedContainer === 'mp4' ? WEBM_MIME_TYPES : MP4_MIME_TYPES;

    const requestMatch = requestedMimeTypes.find((candidate) => {
        return support.hasMimeProbe ? MediaRecorder.isTypeSupported(candidate) : false;
    });

    if (requestMatch) {
        return {
            requestedContainer,
            selectedContainer: requestedContainer,
            mimeType: requestMatch,
            fallbackUsed: false
        };
    }

    const fallbackMatch = fallbackMimeTypes.find((candidate) => {
        return support.hasMimeProbe ? MediaRecorder.isTypeSupported(candidate) : false;
    });

    if (!fallbackMatch) {
        return null;
    }

    return {
        requestedContainer,
        selectedContainer: requestedContainer === 'mp4' ? 'webm' : 'mp4',
        mimeType: fallbackMatch,
        fallbackUsed: true
    };
};

const defaultState = (): RecordingState => {
    return {
        status: 'idle',
        tourId: null,
        requestedContainer: null,
        selectedContainer: null,
        mimeType: null,
        resolution: null,
        frameRate: 30,
        includeAnnotations: true,
        fallbackUsed: false
    };
};

const defaultReconstructionTimelineState = (): ReconstructionTimelineRecordingState => {
    return {
        status: 'idle',
        timelineId: null,
        duration: 0,
        requestedContainer: null,
        selectedContainer: null,
        mimeType: null,
        resolution: null,
        frameRate: 30,
        includeAnnotations: true,
        fallbackUsed: false
    };
};

const registerVideoExportEvents = (events: Events) => {
    let state = defaultState();
    let pending: PendingRecording | null = null;
    let active: ActiveRecording | null = null;
    let reconstructionTimelineState = defaultReconstructionTimelineState();
    let pendingReconstructionTimeline: PendingReconstructionTimelineRecording | null = null;
    let activeReconstructionTimeline: ActiveReconstructionTimelineRecording | null = null;
    let playback: TourPlaybackSnapshot = { status: 'stopped', tourId: null };

    const setState = (patch: Partial<RecordingState>) => {
        state = {
            ...state,
            ...patch
        };
        events.fire('tour.recording.stateChanged', state);
    };

    const resetStateToIdle = () => {
        setState(defaultState());
    };

    const setReconstructionTimelineState = (patch: Partial<ReconstructionTimelineRecordingState>) => {
        reconstructionTimelineState = {
            ...reconstructionTimelineState,
            ...patch
        };
        events.fire('reconstructionTimeline.recording.stateChanged', reconstructionTimelineState);
    };

    const resetReconstructionTimelineStateToIdle = () => {
        setReconstructionTimelineState(defaultReconstructionTimelineState());
    };

    const makeFileName = (options: PendingRecording['options'], policy: RecordingPolicy) => {
        if (options.filename && options.filename.trim().length > 0) {
            return options.filename.trim();
        }

        const ts = formatTimestamp(new Date());
        const ext = policy.selectedContainer === 'mp4' ? 'mp4' : 'webm';
        return `${sanitizeFileSegment(options.tourId)}-tour-${ts}.${ext}`;
    };

    const makeReconstructionTimelineFileName = (options: PendingReconstructionTimelineRecording['options'], policy: RecordingPolicy) => {
        if (options.filename && options.filename.trim().length > 0) {
            return options.filename.trim();
        }

        const ts = formatTimestamp(new Date());
        const ext = policy.selectedContainer === 'mp4' ? 'mp4' : 'webm';
        return `${sanitizeFileSegment(options.timelineId)}-reconstruction-timeline-${ts}.${ext}`;
    };

    const downloadBlob = (blob: Blob, filename: string) => {
        const href = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(href);
    };

    const beginRecording = async (requestedTourId: string) => {
        if (!pending || active) {
            return false;
        }

        if (requestedTourId !== pending.options.tourId) {
            return false;
        }

        const recordingOptions = pending.options;
        const recordingPolicy = pending.policy;

        const sourceCanvas = document.getElementById('canvas') as HTMLCanvasElement | null;
        if (!sourceCanvas || typeof sourceCanvas.captureStream !== 'function') {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Tour video export unavailable',
                message: 'Canvas captureStream is not available in this browser.'
            });
            pending = null;
            events.fire('progressEnd');
            resetStateToIdle();
            return false;
        }

        const target = RESOLUTION_PRESETS[recordingOptions.resolution];
        const compositor = document.createElement('canvas');
        compositor.width = target.width;
        compositor.height = target.height;

        const context = compositor.getContext('2d');
        if (!context) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Tour video export unavailable',
                message: 'Could not create video compositor context.'
            });
            pending = null;
            events.fire('progressEnd');
            resetStateToIdle();
            return false;
        }

        let raf = 0;
        let running = true;

        const drawLoop = () => {
            if (!running) {
                return;
            }

            context.clearRect(0, 0, compositor.width, compositor.height);
            context.drawImage(sourceCanvas, 0, 0, compositor.width, compositor.height);

            if (recordingOptions.includeAnnotations) {
                drawAnnotationOverlay(context, sourceCanvas, target);
            }

            raf = requestAnimationFrame(drawLoop);
        };

        drawLoop();

        const stream = compositor.captureStream(recordingOptions.frameRate);
        const chunks: BlobPart[] = [];

        let recorder: MediaRecorder;
        try {
            recorder = new MediaRecorder(stream, {
                mimeType: recordingPolicy.mimeType,
                videoBitsPerSecond: recordingOptions.resolution === '4k' ? 24_000_000 : recordingOptions.resolution === '1080p' ? 12_000_000 : 8_000_000
            });
        } catch (error) {
            running = false;
            if (raf) {
                cancelAnimationFrame(raf);
            }
            stream.getTracks().forEach(track => track.stop());

            await events.invoke('showPopup', {
                type: 'error',
                header: 'Tour video export failed',
                message: `'${(error as Error)?.message ?? error}'`
            });

            pending = null;
            events.fire('progressEnd');
            resetStateToIdle();
            return false;
        }

        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
                chunks.push(event.data);
            }
        };

        let wasCancelled = false;

        const stopPromise = new Promise<void>((resolve) => {
            recorder.onstop = () => {
                running = false;
                if (raf) {
                    cancelAnimationFrame(raf);
                }
                stream.getTracks().forEach(track => track.stop());

                if (wasCancelled) {
                    chunks.length = 0;
                    events.fire('tour.recording.cancelled', {
                        tourId: recordingOptions.tourId,
                        requestedContainer: recordingPolicy.requestedContainer,
                        selectedContainer: recordingPolicy.selectedContainer,
                        mimeType: recordingPolicy.mimeType,
                        fallbackUsed: recordingPolicy.fallbackUsed,
                        resolution: recordingOptions.resolution,
                        frameRate: recordingOptions.frameRate,
                        includeAnnotations: recordingOptions.includeAnnotations
                    });
                } else {
                    const filename = makeFileName(recordingOptions, recordingPolicy);
                    const blob = new Blob(chunks, { type: recordingPolicy.mimeType });
                    downloadBlob(blob, filename);

                    events.fire('tour.recording.completed', {
                        tourId: recordingOptions.tourId,
                        requestedContainer: recordingPolicy.requestedContainer,
                        selectedContainer: recordingPolicy.selectedContainer,
                        mimeType: recordingPolicy.mimeType,
                        fallbackUsed: recordingPolicy.fallbackUsed,
                        resolution: recordingOptions.resolution,
                        frameRate: recordingOptions.frameRate,
                        includeAnnotations: recordingOptions.includeAnnotations,
                        filename
                    });
                }

                pending = null;
                active = null;
                events.fire('progressEnd');
                resetStateToIdle();
                resolve();
            };
        });

        recorder.start(250);

        active = {
            tourId: recordingOptions.tourId,
            recorder,
            policy: recordingPolicy,
            options: recordingOptions,
            stop: async () => {
                if (recorder.state !== 'inactive') {
                    recorder.stop();
                }
                await stopPromise;
            },
            cancel: async () => {
                wasCancelled = true;
                if (recorder.state !== 'inactive') {
                    recorder.stop();
                }
                await stopPromise;
            },
            cleanup: () => {
                running = false;
                if (raf) {
                    cancelAnimationFrame(raf);
                }
                stream.getTracks().forEach(track => track.stop());
            }
        };

        setState({
            status: 'recording',
            tourId: recordingOptions.tourId,
            requestedContainer: recordingPolicy.requestedContainer,
            selectedContainer: recordingPolicy.selectedContainer,
            mimeType: recordingPolicy.mimeType,
            resolution: recordingOptions.resolution,
            frameRate: recordingOptions.frameRate,
            includeAnnotations: recordingOptions.includeAnnotations,
            fallbackUsed: recordingPolicy.fallbackUsed
        });

        events.fire('tour.recording.started', {
            tourId: recordingOptions.tourId,
            requestedContainer: recordingPolicy.requestedContainer,
            selectedContainer: recordingPolicy.selectedContainer,
            mimeType: recordingPolicy.mimeType,
            fallbackUsed: recordingPolicy.fallbackUsed,
            resolution: recordingOptions.resolution,
            frameRate: recordingOptions.frameRate,
            includeAnnotations: recordingOptions.includeAnnotations
        });

        return true;
    };

    const stopActiveRecording = async () => {
        if (!active) {
            return false;
        }

        setState({ status: 'finalizing' });
        events.fire('progressUpdate', {
            text: 'Finalizing tour video…',
            progress: 100
        });

        try {
            await active.stop();
        } catch {
            active.cleanup();
            active = null;
            pending = null;
            events.fire('progressEnd');
            resetStateToIdle();
        }

        return true;
    };

    const stopActiveReconstructionTimelineRecording = async () => {
        if (!activeReconstructionTimeline) {
            return false;
        }

        setReconstructionTimelineState({ status: 'finalizing' });
        events.fire('progressUpdate', {
            text: 'Finalizing reconstruction timeline video…',
            progress: 100
        });

        try {
            await activeReconstructionTimeline.stop();
        } catch {
            activeReconstructionTimeline.cleanup();
            activeReconstructionTimeline = null;
            pendingReconstructionTimeline = null;
            events.fire('progressEnd');
            resetReconstructionTimelineStateToIdle();
        }

        return true;
    };

    const beginReconstructionTimelineRecording = async () => {
        if (!pendingReconstructionTimeline || activeReconstructionTimeline) {
            return false;
        }

        const recordingOptions = pendingReconstructionTimeline.options;
        const recordingPolicy = pendingReconstructionTimeline.policy;

        const sourceCanvas = document.getElementById('canvas') as HTMLCanvasElement | null;
        if (!sourceCanvas || typeof sourceCanvas.captureStream !== 'function') {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Reconstruction timeline video export unavailable',
                message: 'Canvas captureStream is not available in this browser.'
            });
            pendingReconstructionTimeline = null;
            events.fire('progressEnd');
            resetReconstructionTimelineStateToIdle();
            return false;
        }

        const target = RESOLUTION_PRESETS[recordingOptions.resolution];
        const compositor = document.createElement('canvas');
        compositor.width = target.width;
        compositor.height = target.height;

        const context = compositor.getContext('2d');
        if (!context) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Reconstruction timeline video export unavailable',
                message: 'Could not create video compositor context.'
            });
            pendingReconstructionTimeline = null;
            events.fire('progressEnd');
            resetReconstructionTimelineStateToIdle();
            return false;
        }

        const stream = compositor.captureStream(recordingOptions.frameRate);
        const chunks: BlobPart[] = [];

        let recorder: MediaRecorder;
        try {
            recorder = new MediaRecorder(stream, {
                mimeType: recordingPolicy.mimeType,
                videoBitsPerSecond: recordingOptions.resolution === '4k' ? 24_000_000 : recordingOptions.resolution === '1080p' ? 12_000_000 : 8_000_000
            });
        } catch (error) {
            stream.getTracks().forEach(track => track.stop());

            await events.invoke('showPopup', {
                type: 'error',
                header: 'Reconstruction timeline video export failed',
                message: `'${(error as Error)?.message ?? error}'`
            });

            pendingReconstructionTimeline = null;
            events.fire('progressEnd');
            resetReconstructionTimelineStateToIdle();
            return false;
        }

        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
                chunks.push(event.data);
            }
        };

        let wasCancelled = false;
        let raf = 0;
        let running = true;
        let finalizeScheduled = false;
        const startedAt = performance.now();

        const stopPromise = new Promise<void>((resolve) => {
            recorder.onstop = () => {
                running = false;
                if (raf) {
                    cancelAnimationFrame(raf);
                }
                stream.getTracks().forEach(track => track.stop());

                if (wasCancelled) {
                    chunks.length = 0;
                    events.fire('reconstructionTimeline.recording.cancelled', {
                        timelineId: recordingOptions.timelineId,
                        duration: recordingOptions.duration,
                        requestedContainer: recordingPolicy.requestedContainer,
                        selectedContainer: recordingPolicy.selectedContainer,
                        mimeType: recordingPolicy.mimeType,
                        fallbackUsed: recordingPolicy.fallbackUsed,
                        resolution: recordingOptions.resolution,
                        frameRate: recordingOptions.frameRate,
                        includeAnnotations: recordingOptions.includeAnnotations
                    });
                } else {
                    events.invoke('reconstructionTimeline.setScrubTime', recordingOptions.duration);
                    const filename = makeReconstructionTimelineFileName(recordingOptions, recordingPolicy);
                    const blob = new Blob(chunks, { type: recordingPolicy.mimeType });
                    downloadBlob(blob, filename);

                    events.fire('reconstructionTimeline.recording.completed', {
                        timelineId: recordingOptions.timelineId,
                        duration: recordingOptions.duration,
                        requestedContainer: recordingPolicy.requestedContainer,
                        selectedContainer: recordingPolicy.selectedContainer,
                        mimeType: recordingPolicy.mimeType,
                        fallbackUsed: recordingPolicy.fallbackUsed,
                        resolution: recordingOptions.resolution,
                        frameRate: recordingOptions.frameRate,
                        includeAnnotations: recordingOptions.includeAnnotations,
                        filename,
                        fileSize: blob.size
                    });
                }

                pendingReconstructionTimeline = null;
                activeReconstructionTimeline = null;
                events.fire('progressEnd');
                resetReconstructionTimelineStateToIdle();
                resolve();
            };
        });

        const drawLoop = (now: number) => {
            if (!running) {
                return;
            }

            const elapsedSeconds = Math.max(0, (now - startedAt) / 1000);
            const normalized = Math.min(1, elapsedSeconds / recordingOptions.duration);
            const scrubTime = recordingOptions.duration * normalized;

            events.invoke('reconstructionTimeline.setScrubTime', scrubTime);
            events.fire('progressUpdate', {
                text: `Recording reconstruction timeline ${Math.round(normalized * 100)}%`,
                progress: normalized * 100
            });

            context.clearRect(0, 0, compositor.width, compositor.height);
            context.drawImage(sourceCanvas, 0, 0, compositor.width, compositor.height);

            if (recordingOptions.includeAnnotations) {
                drawAnnotationOverlay(context, sourceCanvas, target);
            }

            if (normalized >= 1) {
                events.invoke('reconstructionTimeline.setScrubTime', recordingOptions.duration);
                if (!finalizeScheduled) {
                    finalizeScheduled = true;
                    stopActiveReconstructionTimelineRecording();
                }
                return;
            }

            raf = requestAnimationFrame(drawLoop);
        };

        recorder.start(250);
        raf = requestAnimationFrame(drawLoop);

        activeReconstructionTimeline = {
            timelineId: recordingOptions.timelineId,
            recorder,
            policy: recordingPolicy,
            options: recordingOptions,
            stop: async () => {
                if (recorder.state !== 'inactive') {
                    recorder.stop();
                }
                await stopPromise;
            },
            cancel: async () => {
                wasCancelled = true;
                if (recorder.state !== 'inactive') {
                    recorder.stop();
                }
                await stopPromise;
            },
            cleanup: () => {
                running = false;
                if (raf) {
                    cancelAnimationFrame(raf);
                }
                stream.getTracks().forEach(track => track.stop());
            }
        };

        setReconstructionTimelineState({
            status: 'recording',
            timelineId: recordingOptions.timelineId,
            duration: recordingOptions.duration,
            requestedContainer: recordingPolicy.requestedContainer,
            selectedContainer: recordingPolicy.selectedContainer,
            mimeType: recordingPolicy.mimeType,
            resolution: recordingOptions.resolution,
            frameRate: recordingOptions.frameRate,
            includeAnnotations: recordingOptions.includeAnnotations,
            fallbackUsed: recordingPolicy.fallbackUsed
        });

        events.fire('reconstructionTimeline.recording.started', {
            timelineId: recordingOptions.timelineId,
            duration: recordingOptions.duration,
            requestedContainer: recordingPolicy.requestedContainer,
            selectedContainer: recordingPolicy.selectedContainer,
            mimeType: recordingPolicy.mimeType,
            fallbackUsed: recordingPolicy.fallbackUsed,
            resolution: recordingOptions.resolution,
            frameRate: recordingOptions.frameRate,
            includeAnnotations: recordingOptions.includeAnnotations
        });

        return true;
    };

    events.function('tour.recording.support', () => {
        const support = createSupportSnapshot();
        const preferred = buildRecordingPolicy('mp4');

        return {
            ...support,
            preferredPolicy: preferred
        };
    });

    events.function('tour.recording.state', () => {
        return state;
    });

    events.function('tour.recording.policy', (requestedContainer?: TourVideoContainer) => {
        const requested = requestedContainer === 'webm' ? 'webm' : 'mp4';
        return buildRecordingPolicy(requested);
    });

    events.function('tour.recording.start', async (options: TourVideoExportOptions) => {
        if (!options || typeof options.tourId !== 'string' || options.tourId.trim().length === 0) {
            throw new Error('tour.recording.start requires a tourId');
        }

        if (pending || active) {
            throw new Error('tour recording is already active');
        }

        const requestedContainer = options.container === 'webm' ? 'webm' : 'mp4';
        const policy = buildRecordingPolicy(requestedContainer);
        if (!policy) {
            throw new Error('No supported MediaRecorder video container/codec is available');
        }

        pending = {
            options: {
                tourId: options.tourId.trim(),
                resolution: options.resolution === '4k' || options.resolution === '720p' ? options.resolution : '1080p',
                container: requestedContainer,
                frameRate: Number.isFinite(options.frameRate) && options.frameRate! > 0 ? Math.min(60, Math.max(12, Math.floor(options.frameRate!))) : 30,
                includeAnnotations: options.includeAnnotations !== false,
                filename: options.filename,
                autoPlay: options.autoPlay !== false
            },
            policy
        };

        setState({
            status: 'armed',
            tourId: pending.options.tourId,
            requestedContainer: policy.requestedContainer,
            selectedContainer: policy.selectedContainer,
            mimeType: policy.mimeType,
            resolution: pending.options.resolution,
            frameRate: pending.options.frameRate,
            includeAnnotations: pending.options.includeAnnotations,
            fallbackUsed: policy.fallbackUsed
        });

        events.fire('progressStart', 'Recording tour video…', true);
        events.fire('progressUpdate', {
            text: policy.fallbackUsed ?
                `Using ${policy.selectedContainer.toUpperCase()} fallback for requested ${policy.requestedContainer.toUpperCase()}` :
                `Using ${policy.selectedContainer.toUpperCase()} output`,
            progress: 0
        });

        if (pending.options.autoPlay) {
            events.fire('tour.play', pending.options.tourId);
        } else if (playback.status === 'playing' && playback.tourId === pending.options.tourId) {
            await beginRecording(pending.options.tourId);
        }

        return {
            tourId: pending?.options.tourId ?? state.tourId,
            requestedContainer: policy.requestedContainer,
            selectedContainer: policy.selectedContainer,
            mimeType: policy.mimeType,
            fallbackUsed: policy.fallbackUsed
        };
    });

    events.function('tour.recording.cancel', async () => {
        if (!pending && !active) {
            return false;
        }

        pending = null;

        if (active) {
            try {
                await active.cancel();
            } catch {
                active.cleanup();
                active = null;
                events.fire('progressEnd');
                resetStateToIdle();
            }
            return true;
        }

        events.fire('progressEnd');
        resetStateToIdle();
        return true;
    });

    events.function('reconstructionTimeline.recording.support', () => {
        const support = createSupportSnapshot();
        const preferred = buildRecordingPolicy('mp4');

        return {
            ...support,
            preferredPolicy: preferred
        };
    });

    events.function('reconstructionTimeline.recording.state', () => {
        return reconstructionTimelineState;
    });

    events.function('reconstructionTimeline.recording.policy', (requestedContainer?: TourVideoContainer) => {
        const requested = requestedContainer === 'webm' ? 'webm' : 'mp4';
        return buildRecordingPolicy(requested);
    });

    events.function('reconstructionTimeline.recording.start', async (options?: ReconstructionTimelineVideoExportOptions) => {
        if (pendingReconstructionTimeline || activeReconstructionTimeline) {
            throw new Error('reconstruction timeline recording is already active');
        }

        const timeline = events.invoke('reconstructionTimeline.get') as ReconstructionTimelineSnapshot | undefined;
        if (!timeline || typeof timeline.id !== 'string' || timeline.id.trim().length === 0) {
            throw new Error('reconstructionTimeline.recording.start requires an existing reconstruction timeline');
        }

        const duration = typeof timeline.duration === 'number' ? timeline.duration : Number.NaN;
        if (!Number.isFinite(duration) || duration <= 0) {
            throw new Error('reconstructionTimeline.recording.start requires timeline duration > 0');
        }

        const requestedContainer = options?.container === 'webm' ? 'webm' : 'mp4';
        const policy = buildRecordingPolicy(requestedContainer);
        if (!policy) {
            throw new Error('No supported MediaRecorder video container/codec is available');
        }

        pendingReconstructionTimeline = {
            options: {
                timelineId: timeline.id.trim(),
                duration,
                resolution: options?.resolution === '4k' || options?.resolution === '720p' ? options.resolution : '1080p',
                container: requestedContainer,
                frameRate: Number.isFinite(options?.frameRate) && options!.frameRate! > 0 ? Math.min(60, Math.max(12, Math.floor(options!.frameRate!))) : 30,
                includeAnnotations: options?.includeAnnotations !== false,
                filename: options?.filename
            },
            policy
        };

        setReconstructionTimelineState({
            status: 'armed',
            timelineId: pendingReconstructionTimeline.options.timelineId,
            duration: pendingReconstructionTimeline.options.duration,
            requestedContainer: policy.requestedContainer,
            selectedContainer: policy.selectedContainer,
            mimeType: policy.mimeType,
            resolution: pendingReconstructionTimeline.options.resolution,
            frameRate: pendingReconstructionTimeline.options.frameRate,
            includeAnnotations: pendingReconstructionTimeline.options.includeAnnotations,
            fallbackUsed: policy.fallbackUsed
        });

        events.fire('progressStart', 'Recording reconstruction timeline video…', true);
        events.fire('progressUpdate', {
            text: policy.fallbackUsed ?
                `Using ${policy.selectedContainer.toUpperCase()} fallback for requested ${policy.requestedContainer.toUpperCase()}` :
                `Using ${policy.selectedContainer.toUpperCase()} output`,
            progress: 0
        });

        events.invoke('reconstructionTimeline.setScrubTime', 0);
        const started = await beginReconstructionTimelineRecording();
        if (!started) {
            throw new Error('reconstruction timeline recording failed to begin');
        }

        return {
            timelineId: pendingReconstructionTimeline?.options.timelineId ?? reconstructionTimelineState.timelineId,
            duration,
            requestedContainer: policy.requestedContainer,
            selectedContainer: policy.selectedContainer,
            mimeType: policy.mimeType,
            fallbackUsed: policy.fallbackUsed
        };
    });

    events.function('reconstructionTimeline.recording.cancel', async () => {
        if (!pendingReconstructionTimeline && !activeReconstructionTimeline) {
            return false;
        }

        pendingReconstructionTimeline = null;

        if (activeReconstructionTimeline) {
            try {
                await activeReconstructionTimeline.cancel();
            } catch {
                activeReconstructionTimeline.cleanup();
                activeReconstructionTimeline = null;
                events.fire('progressEnd');
                resetReconstructionTimelineStateToIdle();
            }
            return true;
        }

        events.fire('progressEnd');
        resetReconstructionTimelineStateToIdle();
        return true;
    });

    events.on('tour.playbackChanged', (snapshot?: TourPlaybackSnapshot) => {
        playback = {
            status: snapshot?.status ?? 'stopped',
            tourId: snapshot?.tourId ?? null
        };
    });

    events.on('tour.playing', async (tourId?: string) => {
        if (!pending || active) {
            return;
        }

        if (tourId && tourId === pending.options.tourId) {
            await beginRecording(tourId);
        }
    });

    events.on('tour.progress', (progress: number, _stepIndex: number, tourId?: string) => {
        if (!active || !tourId || tourId !== active.tourId) {
            return;
        }

        const normalized = Number.isFinite(progress) ? Math.max(0, Math.min(1, progress)) : 0;
        events.fire('progressUpdate', {
            text: `Recording ${Math.round(normalized * 100)}%`,
            progress: normalized * 100
        });
    });

    events.on('tour.stopped', async () => {
        if (!active) {
            if (pending) {
                pending = null;
                events.fire('progressEnd');
                resetStateToIdle();
            }
            return;
        }

        await stopActiveRecording();
    });

    events.on('progressCancel', async () => {
        if (!pending && !active && !pendingReconstructionTimeline && !activeReconstructionTimeline) {
            return;
        }

        if (pending || active) {
            events.fire('tour.stop');
            await events.invoke('tour.recording.cancel');
        }

        if (pendingReconstructionTimeline || activeReconstructionTimeline) {
            await events.invoke('reconstructionTimeline.recording.cancel');
        }
    });

    events.on('scene.clear', async () => {
        if (pending || active) {
            await events.invoke('tour.recording.cancel');
        }
        if (pendingReconstructionTimeline || activeReconstructionTimeline) {
            await events.invoke('reconstructionTimeline.recording.cancel');
        }
    });
};

export { registerVideoExportEvents };
